import { AloftArea, isDefined, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectEditablePropertyDataEntities } from './editablePropertyDataSelectors.ts';

export const selectSiteEntities = createSelector(
  [selectEditablePropertyDataEntities],
  (editablePropertyDataEntities) =>
    Object.fromEntries(
      Object.entries(editablePropertyDataEntities).map(([id, editableData]) => [
        id,
        { id, ...editableData.site },
      ]),
    ),
);

export const selectSiteById = createSelector(
  [selectSiteEntities, (_state, id: string) => id],
  (siteEntities, id) => siteEntities[id],
);

export const selectAllSite = createSelector([selectSiteEntities], (siteEntities) =>
  Object.values(siteEntities),
);

const getSiteDisplayFromEntities = (
  siteEntities: ReturnType<typeof selectSiteEntities>,
  id: string,
) => {
  const siteForTransaction = siteEntities[id];
  if (isUndefined(siteForTransaction)) return {};

  const { area, location, view, notes } = siteForTransaction;

  return {
    area: isDefined(area) ? AloftArea.schema.parse(area).getSiteAreaString() : '',
    location: location?.trim().length === 0 ? undefined : location,
    view: view?.trim().length === 0 ? undefined : view,
    notes: notes?.trim().length === 0 ? undefined : notes,
  };
};

export const selectSiteDisplay = createSelector(
  [selectSiteEntities, (_state, id: string) => id],
  (site, id) => {
    const siteForTransaction = site[id];
    if (isUndefined(siteForTransaction)) return {};

    return getSiteDisplayFromEntities(site, id);
  },
);

export const selectSitesDisplay = createSelector(
  [selectSiteEntities, selectCompSequence],
  (site, compSequence) => {
    const properties = ['subject', ...compSequence];

    return Object.fromEntries(
      properties.map((propID) => [propID, getSiteDisplayFromEntities(site, propID)]),
    );
  },
);

export const selectNumericSiteArea = createSelector(
  [selectSiteEntities, (_state, id: string) => id],
  (site, id) => {
    const siteForTransaction = site[id];
    if (isUndefined(siteForTransaction)) return undefined;

    const { area } = siteForTransaction;

    return isDefined(area) ? AloftArea.schema.parse(area).getUnitAmount('sqft') : undefined;
  },
);
